import {
	catchError,
	from,
	map,
	Observable,
	of,
	switchMap,
	throwError,
} from "rxjs";
import { UserPhoto } from "../hooks/usePhotoGallery";
import { first, isEmpty, map as _map, split, isNil } from "lodash";
import { v4 } from "uuid";
import { blobToBase64 } from "../utils";
import { ThemeOption } from "../context";

export interface PartDetailsMetadata {
	spare_part_max_count?: number;
}

export interface SparePartStatusResponse {
	spare_part_count: number;
	spare_part_max_count: number;
}

export interface PartDetails {
	name: string;
	description: string;
	metadata?: PartDetailsMetadata;
	photos: UserPhoto[];
}

export interface PartDetailsResponse {
	metadata: PartDetailsMetadata;
	content: PartPhoto[];
}
export interface PartPhoto {
	blob_metadata?: { [key: string]: string };
	blob_sas_url: string;
	image_uuid4: string;
}

export interface SearchResultDetail extends SearchResultItem {
	masterData: string;
	warehouseInfo: string;
}

export interface SearchResults {
	data: SearchResultItem[];
	embeddings: string;
}

interface SearchResultResponse {
	data: SearchResultItemResponse[];
	embeddings: string;
}

interface SearchResultItemResponse {
	blob_metadata: any;
	blob_sas_urls: string[];
	drawing: number[];
	image_uuid4: string;
}

export interface SearchResultItem {
	barcode: string;
	photos: string[];
}

export interface SearchHistoryItem {
	status: "OK" | "NOK";
	timestamp: string;
}

export interface StockInfo {
	batch: string;
	plan: string;
	plant: string;
	storageLocation: string;
	quantity: number;
	uom: string;
}

export interface PartWarehouseInfo {
	description: string;
	stockInfos: StockInfo[];
}

export interface RetrievalAccuracy {
	missing: number;
	top1_accuracy: number;
	top5_accuracy: number;
	top10_accuracy: number;
	top1_count: number;
	top5_count: number;
	top10_count: number;
	missed_count: number;
}

export interface AzureUserRole {
	id: string;
}

export interface UserSettingsResponse {
	detail?: string;
	language: string;
	my_plant: string;
	theme: string;
}

export interface UserSettingsRequest {
	user: string;
	language: string;
	theme: ThemeOption;
	my_plant: string;
}

const API_HOST = process.env.REACT_APP_API_HOST;

function getPartDetails(
	id: string,
	accessToken: string
): Observable<PartDetails> {
	return from(
		fetch(`${API_HOST}/list-folder-content?barcode=${id}`, {
			method: "POST",
			headers: {
				Accept: "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
		})
	).pipe(
		switchMap((response) => response.json()),
		map((response: PartDetailsResponse) => ({
			name: id,
			description: `${id} description`,
			metadata: response.metadata,
			photos: _map(response.content, (photo) => ({
				blobPath: photo.blob_sas_url,
				filepath: photo.image_uuid4,
				metadata: photo.blob_metadata,
			})),
		}))
	);
}

function postPhoto(
	partId: string,
	base64: string,
	isOcr: boolean,
	coordinates: number[] = [],
	accessToken: string,
	username: string,
	plant_number: string
): Observable<SparePartStatusResponse> {
	const requestBody = {
		base64_encoded_image_string: base64,
		barcode: partId,
		description: "test description",
		drawing: coordinates,
		is_ocr: isOcr ?? false,
		image_uuid4: v4(),
		material_number: "3453453",
		//plant_number: "485",
		plant_number,
		upload_date: new Date(),
		username,
	};

	return from(
		fetch(`${API_HOST}/upload-single-image`, {
			// fetch(`${API_HOST}/upload-single-image?blob_token=${BLOB_TOKEN}`, {
			method: "POST",
			body: JSON.stringify(requestBody),
			headers: {
				Authorization: `Bearer ${accessToken}`,
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		})
	).pipe(switchMap((response) => response.json()));
}

function deletePhoto(
	partId: string,
	filename: string,
	accessToken: string
): Observable<any> {
	return from(
		fetch(
			`${API_HOST}/delete-single-image?barcode=${partId}&image_id=${filename}`,
			{
				// fetch(`${API_HOST}/upload-single-image?blob_token=${BLOB_TOKEN}`, {
				method: "DELETE",
				headers: {
					Authorization: `Bearer ${accessToken}`,
					Accept: "application/json",
					"Content-Type": "application/json",
				},
			}
		)
	);
}

function login(username: string, password: string) {
	const requestBody = {
		username,
		password,
	};

	return from(
		fetch(`${API_HOST}/login`, {
			method: "POST",
			body: JSON.stringify(requestBody),
			headers: {
				Authorization: `Bearer ${process.env.REACT_APP_BLOB_TOKEN}`,
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		})
	).pipe(
		switchMap((result) => {
			if (result.status !== 200) {
				return throwError(() => ({ message: "Invalid Credentials " }));
			}
			return of(result);
		})
	);
}

export interface MetadataObject {
	Material: string;
	Description: string;
	"Manufacturer Part Number": string;
	Result: string;
	Message: string;
}

function getDescriptions(barcode: string): Observable<MetadataObject> {
	// read from csv file
	return from(fetch('/sensors.csv'))
		.pipe(
			switchMap(response => {
				if (!response.ok) {
					throw new Error('Failed to fetch CSV file');
				}
				return response.text();
			}),
			map(csvText => {
				const rows = csvText.split('\n');
				const found = rows.find(row => row.split(';')[0] === barcode);
				if (!found) {
					return {
						Material: '',
						Description: '',
						'Manufacturer Part Number': '',
						Result: 'OK',
						Message: 'Not found in Database'
					};
				}

				const values = found.split(';');
				return {
					Material: values[0] || '',
					Description: values[1] || '',
					'Manufacturer Part Number': values[1] || '',
					Result: 'OK',
					Message: "Success"
				};
			})
		);
}

function queryMasterdata(
	barcode: string,
	accessToken: string
): Observable<MetadataObject> {
	// call getDescriptions
	return getDescriptions(barcode);

	// return empty object without querying
	return of({
		Material: "123456",
		Description: "Sweet",
		"Manufacturer Part Number": "123456",
		Result: "OK",
		Message: "Success",

	});



	return from(
		fetch(`${API_HOST}/query_metadata?barcode=${barcode}`, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${accessToken}`,
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		})
	).pipe(
		switchMap((response) => response.json()),
		switchMap((metadata: MetadataObject) => {
			if (metadata.Result === "Error") {
				return throwError(() => metadata.Message);
			}
			return of(metadata);
		})
	);
}

function storeAsText(
	barcode: string,
	blobId: string,
	accessToken: string
): Observable<any> {
	return from(
		fetch(
			`${API_HOST}/store-text?barcode=${barcode}&image_uuid=${first(
				split(blobId, ".")
			)}`,
			{
				method: "POST",
				headers: {
					Authorization: `Bearer ${accessToken}`,
					Accept: "application/json",
					"Content-Type": "application/json",
				},
			}
		)
	);
}

export interface ImageUpdateData {
	barcode: string;
	blobId: string;
	isOcr: boolean;
	drawing: number[];
	signedUrl: string;
	username: string;
}

function updateImage(updateData: ImageUpdateData, accessToken: string) {
	const { barcode, blobId, drawing, isOcr, signedUrl, username } = updateData;

	return from(
		fetch(
			// `${API_HOST}/update-image?barcode=${barcode}&image_uuid=${blobId}&is_ocr=${isOcr}&image_signed_url=${signedUrl}`,
			`${API_HOST}/update-image`,
			{
				method: "POST",
				body: JSON.stringify({
					barcode: barcode,
					blob_metadata: {},
					image_uuid: blobId,
					is_ocr: isOcr,
					drawing,
					image_signed_url: signedUrl,
					username,
				}),
				headers: {
					Authorization: `Bearer ${accessToken}`,
					Accept: "application/json",
					"Content-Type": "application/json",
				},
			}
		)
	);
}

export interface SearchResultRequest {
	accessToken: string;
	drawing: number[];
	embedding: string;
	page: number;
	pageSize: number;
	useOcr: boolean;
	username: string;
	searchId: string;
	searchTerm?: string;
	webPath: string;
}

export interface TextSearchResultRequest {
	accessToken: string;
	page: number;
	pageSize: number;
	searchTerm: string;
	username: string;
}

function getSearchResults({
	accessToken,
	drawing,
	embedding,
	page,
	pageSize,
	searchId,
	useOcr,
	username,
	webPath,
}: SearchResultRequest): Observable<SearchResults> {
	return from(fetch(webPath)).pipe(
		switchMap((response) => response.blob()),
		switchMap((blob) => blobToBase64(blob)),
		switchMap((base64String: string) => {
			return from(
				fetch(`${API_HOST}/request_search`, {
					method: "POST",
					body: JSON.stringify({
						image_uuid: searchId,
						base64_encoded_image_string: base64String,
						drawing: drawing,
						embeddings: embedding,
						page: page,
						page_size: pageSize,
						use_ocr: useOcr,
						username,
					}),
					headers: {
						Authorization: `Bearer ${accessToken}`,
						Accept: "application/json",
						"Content-Type": "application/json",
					},
				})
			);
		}),
		switchMap((response) => response.json()),
		map((response: SearchResultResponse) => {
			const { data: list, embeddings } = response;
			return {
				data: _map(list, (item) => {
					return {
						barcode: item.image_uuid4,
						photos: item.blob_sas_urls,
					};
				}),
				embeddings: embeddings,
			};
		})
	);
}

function getSearchResultsByText({
	accessToken,
	searchTerm,
	page,
	pageSize,
	username
}: TextSearchResultRequest): Observable<SearchResults> {
	return from(
		fetch(`${API_HOST}/request_search_by_text`, {
			method: "POST",
			body: JSON.stringify({
				username: username,
				search_text: searchTerm,
				page: page,
				page_size: pageSize,
			}),
			headers: {
				Authorization: `Bearer ${accessToken}`,
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		})
	).pipe(
		switchMap((response) => response.json()),
		map((response: SearchResultResponse) => {
			const { data: list, embeddings } = response;
			return {
				data: _map(list, (item) => {
					return {
						barcode: item.image_uuid4,
						photos: item.blob_sas_urls,
					};
				}),
				embeddings: embeddings,
			};
		})
	);
}

function getWarehouseInfo(barcode: string): Observable<PartWarehouseInfo> {
	return of({
		description: "BEARING-ROLLER,CAZ243",
		stockInfos: [
			{
				batch: "B-11-03-07",
				plan: "1166",
				plant: "Edelstal PRD",
				storageLocation: "6200",
				quantity: 1,
				uom: "EA",
			},
			{
				batch: "B-11-03-07",
				plan: "1166",
				plant: "Edelstal PRD",
				storageLocation: "6298",
				quantity: 0,
				uom: "EA",
			},
			{
				batch: "",
				plan: "5511",
				plant: "Dietlikon PRD",
				storageLocation: "6200",
				quantity: 0,
				uom: "EA",
			},
		],
	});
}

function rateSearch(
	barcode: string,
	searchId: string,
	selectedIndex: string,
	accessToken: string
) {
	const barcodeQueryString = isEmpty(barcode) ? "" : `&barcode=${barcode}`;

	return from(
		fetch(
			`${API_HOST}/select_id?selected_index=${selectedIndex}${barcodeQueryString}&image_uuid=${searchId}`,
			{
				method: "POST",
				headers: {
					Authorization: `Bearer ${accessToken}`,
					Accept: "application/json",
					"Content-Type": "application/json",
				},
			}
		)
	);
}

function getRetrievalAccuracy(
	accessToken: string
): Observable<RetrievalAccuracy> {
	return from(
		fetch(`${API_HOST}/get-performance`, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${accessToken}`,
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		})
	).pipe(switchMap((response) => response.json()));
}

function resetPerformance(accessToken: string): Observable<boolean> {
	return from(
		fetch(`${API_HOST}/reset-performance`, {
			method: "DELETE",
			headers: {
				Authorization: `Bearer ${accessToken}`,
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		})
	).pipe(
		catchError((error) => {
			console.error(error);
			return of(false);
		}),
		map(() => true)
	);
}

function getProcessStatus(accessToken: string): Observable<boolean> {
	return from(
		fetch(`${API_HOST}/get-process-status`, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${accessToken}`,
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		})
	).pipe(
		switchMap((response) => response.text()),
		map((text) => {
			return text === '"idle"';
		})
	);
}

function triggerIndexImages(accessToken: string): Observable<boolean> {
	return from(
		fetch(`${API_HOST}/index-images`, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${accessToken}`,
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		})
	).pipe(
		switchMap((response) => response.text()),
		map((text) => {
			return text === '"start processing"' || text === '"processing"';
		})
	);
}

export interface PlantStock {
	Plant: string;
	StorageLocation: string;
	Batch: string;
	MatlWrhsStkQtyInMatlBaseUnit: string;
}

export interface MaterialStock {
	Material: string;
	MaterialBaseUnit: string;
	to_MatlStkInAcctMod: {
		results: PlantStock[];
	};
}

function getMaterialStock(
	barcode: string,
	accessToken: string
): Observable<MaterialStock> {
	return of({
		Material: "123456",
		MaterialBaseUnit: "EA",
		to_MatlStkInAcctMod: {
			results: [
				{
					Plant: "0001",
					StorageLocation: "0001",
					Batch: "0001",
					MatlWrhsStkQtyInMatlBaseUnit: "1",
				},
				{
					Plant: "0002",
					StorageLocation: "0002",
					Batch: "0002",
					MatlWrhsStkQtyInMatlBaseUnit: "2",
				},
			],
		},
	});
	return from(
		fetch(`${API_HOST}/material_stock?barcode=${barcode}`, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${accessToken}`,
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		})
	).pipe(switchMap((response) => response.json()));
}

function postDebugPhoto(
	base64: string,
	debugSessionId: string,
	accessToken: string,
	username: string,
	isCorrect: boolean
) {
	const requestBody = {
		base64_encoded_image_string: base64,
		is_correct: isCorrect,
		session_id: debugSessionId,
		username,
	};

	return from(
		fetch(`${API_HOST}/upload-debug-image`, {
			method: "POST",
			body: JSON.stringify(requestBody),
			headers: {
				Authorization: `Bearer ${accessToken}`,
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		})
	);
}

function getUserSettings(
	accessToken: string,
	username: string
): Observable<UserSettingsResponse> {
	return from(
		fetch(`${API_HOST}/ui_settings/get?user_name=${username}`, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${accessToken}`,
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		})
	).pipe(
		switchMap((response) => response.json()),
		map((response) => response.message)
	);
}

function getAzureUserRoles(accessToken: string): Observable<AzureUserRole[]> {
	return from(
		fetch("https://graph.microsoft.com/v1.0/me/memberOf", {
			method: "GET",
			headers: {
				Authorization: `Bearer ${accessToken}`,
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		})
	).pipe(
		switchMap((response) => {
			return response.json();
		}),
		map((response) => {
			if (isNil(response.value)) {
				console.error("No Azure Roles found");
				return [];
			}
			return response.value;
		})
	);
}

function postUserSettings(
	accessToken: string,
	request: UserSettingsRequest
): Observable<Response> {
	return from(
		fetch(`${API_HOST}/ui_settings/update`, {
			method: "PUT",
			body: JSON.stringify(request),
			headers: {
				Authorization: `Bearer ${accessToken}`,
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		})
	);
}

function updateDocs(
	docsUrl: string,
	accessToken: string
): Observable<Response> {
	const requestBody = {
		url: docsUrl,
	};

	return from(
		fetch(`${API_HOST}/update-documentation`, {
			method: "PUT",
			body: JSON.stringify(requestBody),
			headers: {
				Authorization: `Bearer ${accessToken}`,
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		})
	).pipe(
		switchMap(async (response) => {
			if (!response.ok) {
				const data = await response.json();
				const detail = data.detail;
				throw new Error(detail || "Failed to update Docs!");
			}

			return response.json();
		})
	);
}

function getDocs(accessToken: string): Observable<Response> {
	return from(
		fetch(`${API_HOST}/get-documentation`, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${accessToken}`,
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		})
	).pipe(
		switchMap(async (response) => {
			if (!response.ok) {
				const data = await response.json();
				const detail = data.detail;
				throw new Error(detail || "Failed to get Docs!");
			}

			return response.json();
		})
	);
}

export {
	deletePhoto,
	getAzureUserRoles,
	getMaterialStock,
	getPartDetails,
	getProcessStatus,
	getRetrievalAccuracy,
	getSearchResults,
	getSearchResultsByText,
	getUserSettings,
	getWarehouseInfo,
	login,
	postDebugPhoto,
	postPhoto,
	postUserSettings,
	resetPerformance,
	queryMasterdata,
	rateSearch,
	storeAsText,
	triggerIndexImages,
	updateImage,
	updateDocs,
	getDocs,
};
